<template>
  <div class="row-edit-modal-sidebar tabs tabs--full-height">
    <div
      class="tabs__header"
      role="tablist"
      :aria-label="$t('taskPanel.activity')"
      @keydown="navigateTabs"
    >
      <button
        v-for="sidebarType in sidebarTypes"
        :id="`task-tab-${$.uid}-${sidebarType.getType()}`"
        :key="sidebarType.getType()"
        type="button"
        role="tab"
        class="task-modal__activity-tab"
        :class="{
          'task-modal__activity-tab--active': activeSidebarType === sidebarType,
        }"
        :aria-selected="activeSidebarType === sidebarType"
        :aria-controls="`task-activity-${$.uid}`"
        :tabindex="activeSidebarType === sidebarType ? 0 : -1"
        @click="selectedType = sidebarType.getType()"
      >
        {{ sidebarType.getName() }}
      </button>
    </div>
    <div
      v-if="activeSidebarType"
      :id="`task-activity-${$.uid}`"
      class="tab task-modal__activity-panel"
      role="tabpanel"
      :aria-labelledby="`task-tab-${$.uid}-${activeSidebarType.getType()}`"
    >
      <component
        :is="activeSidebarType.getComponent()"
        :key="activeSidebarType.getType()"
        :row="effectiveRow"
        :table="effectiveTable"
        :database="effectiveDatabase"
        :fields="effectiveFields"
        :view="view"
      />
    </div>
  </div>
</template>

<script>
import FieldService from '@baserow/modules/database/services/field'
import { populateField } from '@baserow/modules/database/store/field'

export default {
  name: 'RowEditModalSidebar',
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    row: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: false,
      default: null,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      // Origem Flow: campos da tabela de origem, quando a linha é do espelho
      // "Todas as tarefas" (o histórico precisa deles para nomear as colunas).
      selectedType: null,
      sourceFields: [],
      sourceFieldsTableId: null,
    }
  },
  computed: {
    /**
     * Origem Flow: na tabela "Todas as tarefas" (espelho agregado) os
     * comentários e o histórico são os da linha de origem — a linha do
     * espelho é só um reflexo dela.
     */
    sourceContext() {
      const mirror = this.table.all_tasks
      if (!mirror || !mirror.fields) {
        return null
      }
      const tableId = parseInt(this.row[`field_${mirror.fields.source_table}`])
      const rowId = parseInt(this.row[`field_${mirror.fields.source_row}`])
      if (!tableId || !rowId) {
        return null
      }
      for (const application of this.$store.getters['application/getAll']) {
        const table = (application.tables || []).find((t) => t.id === tableId)
        if (table) {
          return { database: application, table, rowId }
        }
      }
      return null
    },
    effectiveRow() {
      return this.sourceContext
        ? { ...this.row, id: this.sourceContext.rowId }
        : this.row
    },
    effectiveTable() {
      return this.sourceContext ? this.sourceContext.table : this.table
    },
    effectiveDatabase() {
      return this.sourceContext ? this.sourceContext.database : this.database
    },
    effectiveFields() {
      if (!this.sourceContext) {
        return this.fields
      }
      return this.sourceFieldsTableId === this.sourceContext.table.id
        ? this.sourceFields
        : []
    },
    activeSidebarType() {
      return (
        this.sidebarTypes.find(
          (type) => type.getType() === this.selectedType
        ) || this.sidebarTypes[this.selectedTabIndex]
      )
    },
    selectedTabIndex() {
      const types = this.sidebarTypes
      const index = types.findIndex((type) =>
        type.isSelectedByDefault(this.database, this.table)
      )
      return Math.max(index, 0)
    },
    sidebarTypes() {
      const allSidebarTypes = this.$registry.getOrderedList('rowModalSidebar')
      return allSidebarTypes.filter(
        (type) =>
          type.isDeactivated(
            this.database,
            this.table,
            this.readOnly,
            this.view
          ) === false && type.getComponent()
      )
    },
  },
  watch: {
    'sourceContext.table.id': {
      handler(tableId) {
        this.loadSourceFields(tableId)
      },
      immediate: true,
    },
  },
  methods: {
    navigateTabs(event) {
      const buttons = Array.from(
        event.currentTarget.querySelectorAll('[role="tab"]')
      )
      const index = buttons.indexOf(event.target)
      if (
        index < 0 ||
        !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)
      )
        return
      event.preventDefault()
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? buttons.length - 1
            : (index + (event.key === 'ArrowRight' ? 1 : -1) + buttons.length) %
              buttons.length
      buttons[next].click()
      buttons[next].focus()
    },
    async loadSourceFields(tableId) {
      if (!tableId || tableId === this.sourceFieldsTableId) {
        return
      }
      try {
        const { data } = await FieldService(this.$client).fetchAll(tableId)
        data.forEach((field) => populateField(field, this.$registry))
        this.sourceFields = data
        this.sourceFieldsTableId = tableId
      } catch (error) {
        // Sem os campos da origem o histórico mostra só os ids.
        this.sourceFields = []
        this.sourceFieldsTableId = tableId
      }
    },
  },
}
</script>
